import { ColorType, PriceScaleMode } from 'lightweight-charts'
import React, { forwardRef, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CHART_PRICE_SCALE_MARGINS, chartLayoutOptions, ChartViewKey, subscriptionTags } from '../../../assets/constants'
import { useDispatch, useSelector } from 'react-redux'
import { Button, useTheme } from '@mui/material'
import ScrollToRecentBarButton from '../../chartV2/scrollToRecentBarButton/ScrollToRecentBarButton'
import './chartWrapper.css'
import { AppContext } from '../../../App'
import { selectTimezone } from '../../../selectors/common'
import { getSecuritySnapshot } from '../../../selectors/security'
import {
    buildCrosshairMultiStrikeStringSnapshot,
    buildLatestMultiStrikeStringSnapshot,
} from '../buildMultiStrikeStringSnapshot'
import {
    bumpMultiStrikeCrosshairRevision,
    clearMultiStrikeChartSnapshot,
    setMultiStrikeChartCrosshairSnapshot,
    setMultiStrikeChartLiveSnapshot,
} from '../multiStrikeChartSnapshotStore'
import { WebSocketContext, WebSocketContextValue } from '../../webSocketConnection/WebSocket'
import websocketActions from '../../../actions/websocket.actions'
import Chart from '../chart'
import { selectPrimarySecurity } from '../../../selectors/home'
import {
    selectMultiStrikeAtm,
    selectMultiStrikeChartCallPut,
    selectMultiStrikeChartSecurities,
    selectMultiStrikeChartStyles,
    selectMultiStrikeChartTimebucket,
    selectMultiStrikeChartType,
    selectMultiStrikeCumOi,
    selectMultiStrikeKey,
    selectMultiStrikeLoading,
    selectMultiStrikeNearAtm,
    selectMultiStrikeShowChange
} from '../../../selectors/multiStrikeChart'
import { ChartMetadata } from './chartMetadata/chartMetadata'
import { exchangeFromJSON } from '../../../proto/generated/common'
import { selectUnderlyingOptionsExpiriesV2 } from '../../../selectors/options'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { callNative } from '../../../util/nativeBridge'
import { getStrikeAndStrikeData } from '../../../pages/home/openInterestHeader/MultiStrikesMenu/MultiStrikesMenu.tsx'
import { MultiStrikeSecuritiesType, setMultiStrikeChartKey, setMultiStrikeChartLoading, setMultiStrikeSecurities } from '../../../reducer/multiStrikeChart.reducer'
import { SearchParamsContext } from '../../providers/searchParamsProvider'
import {
    selectChartTimeBucket,
    selectMultiStrikeChartConfigAtm,
    selectMultiStrikeChartConfigCallPut,
    selectMultiStrikeChartConfigChartType,
    selectMultiStrikeChartConfigCumOi,
    selectMultiStrikeChartConfigExpiry,
    selectMultiStrikeChartConfigNearAtm,
    selectMultiStrikeChartConfigSecurities,
    selectMultiStrikeChartConfigShowChange,
    selectMultiStrikeChartConfigStyles,
    selectMultiStrikeConfigChartKey,
    selectMultiStrikeScalesAndLinesSettings,
    selectMultiStrikeCanvasSettings,
    selectTimeBuckets,
    selectTradingCalendarDataByExchange
} from '../../../selectors/chart'
import { areTwoRangesAlmostSame, CrossHairInfo, DataUpdateRequestType, ViewportCandleInfo } from '../../../util/chart'
import ChartSettingsButtons from '../../chartV2/chartContextMenuButton/ChartSettingsButtons'
import { setChartLoading, updateChartTimeBucket, updateMultiStrikeChartConfig } from '../../../reducer/chart.reducer'
import { primarySecColorDarkTheme, primarySecColorLightTheme } from '../styles'
import { findNearestNumber } from '../../../util/tradepanel'
import { NonInteractiveTooltip } from '../../custom'
import SettingsOutlinedIcon from '../../../assets/icons/SettingOutlined'
import MultiStrikeSettingsDialog from '../../widgetDashboard/widgets/multiStrikeWidget/multiStrikeSettingsDialog'
import { debounce } from 'throttle-debounce'
import { resolveMultiStrikeTimeBucket } from '../../../util/timeBuckets'
import { selectIsEmbedded } from '../../../selectors/device.ts'
import { clearMultiStrikeMetadataUpdaters } from '../multiStrikeChartMetadataDom'
import { buildCanvasLayoutPartial } from '../../../util/canvasSettingsDefaults'
import MultiStrikeChartSkeletonOverlay from '../../common/MultiStrikeChartSkeletonOverlay'

export type ChartContextType = {
    hoverActive: boolean
    // chart: Chart,
    viewKey: string
    displayCumulativeToggle?: boolean
}

const ChartWrapper = forwardRef<any, any>((props, ref) => {
    const { viewKey, isWidget = false, securityList, onCrosshairMove, onTimeRangeChange, persistViewport = true, allowSplit, displayCumulativeToggle = true, onSecuritySelected } = props
    const dispatch = useDispatch()
    const ws = useContext(WebSocketContext)
    const appContext = useContext(AppContext)
    const memoizedViewKey = useMemo(() => viewKey, [viewKey])
    const [container, setContainer] = useState<HTMLDivElement>()
    const memoizedSecurityList = useMemo(() => securityList, [securityList])
    const handleRef = useCallback((ref: HTMLDivElement) => {
        setContainer(ref)
    }, [])
    const { key } = useSelector(selectPrimarySecurity)
    const secKey = useMemo(() => {
        if (isWidget) {
            return memoizedSecurityList[0]
        }
        return key
    }, [isWidget, key, memoizedSecurityList])
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (secKey) getSecurityAsync(secKey).then(setSecurity); else setSecurity(undefined) }, [secKey])
    const multiStrikeChartSecurities = useSelector(isWidget ? selectMultiStrikeChartConfigSecurities(viewKey) : selectMultiStrikeChartSecurities)
    const callPut = useSelector(isWidget ? selectMultiStrikeChartConfigCallPut(viewKey) : selectMultiStrikeChartCallPut)
    const showChange = useSelector(isWidget ? selectMultiStrikeChartConfigShowChange(viewKey) : selectMultiStrikeShowChange)
    const chartType = useSelector(isWidget ? selectMultiStrikeChartConfigChartType(viewKey) : selectMultiStrikeChartType)
    const chartStyles = useSelector(isWidget ? selectMultiStrikeChartConfigStyles(viewKey) : selectMultiStrikeChartStyles)
    const scalesAndLinesSettings = useSelector(selectMultiStrikeScalesAndLinesSettings(viewKey))
    const canvasSettings = useSelector(selectMultiStrikeCanvasSettings(viewKey))
    const multiStikeChartKey = useSelector(isWidget ? selectMultiStrikeConfigChartKey(viewKey) : selectMultiStrikeKey)
    const underlyingToken = getSecurityKey(security?.exchange ?? 0, security?.code ?? '')
    const optionsExpiries = useSelector(selectUnderlyingOptionsExpiriesV2(underlyingToken))
    const { searchParams, setSearchParams } = useContext(SearchParamsContext)!
    const nearAtm = useSelector(isWidget ? selectMultiStrikeChartConfigNearAtm(viewKey) : selectMultiStrikeNearAtm)
    const atm = useSelector(isWidget ? selectMultiStrikeChartConfigAtm(viewKey) : selectMultiStrikeAtm)
    const cumOi = useSelector(isWidget ? selectMultiStrikeChartConfigCumOi(viewKey) : selectMultiStrikeCumOi)
    const configExpiry = useSelector(selectMultiStrikeChartConfigExpiry(viewKey))
    const [openSettings, setOpenSettings] = useState(false)
    const expiry = useMemo(() => {
        if (isWidget) {
            return configExpiry
        }
        return decodeURIComponent(searchParams.get('expiry') || '')
    }, [configExpiry, isWidget, searchParams?.get('expiry'), searchParams.toString()])
    const expiryObj = useMemo(() => {
        return optionsExpiries?.find((o) => o.expiry.name === expiry)?.expiry
    }, [expiry, optionsExpiries])
    const isEmbedded = useSelector(selectIsEmbedded)
    const timeBucket = useSelector(isWidget ? selectChartTimeBucket(viewKey) : selectMultiStrikeChartTimebucket)
    const timeBuckets = useSelector(selectTimeBuckets)
    const timezone = useSelector(selectTimezone)
    const chart = useRef<Chart>(undefined)
    const calendarData = useSelector(selectTradingCalendarDataByExchange(security?.exchange))
    const loading = useSelector(selectMultiStrikeLoading)
    const chartRef = useRef<HTMLDivElement>(null)
    const [scaleDims, setScaleDims] = useState<{ width: number; height: number }>({ width: 52, height: 28 })
    const [leftScaleWidth, setLeftScaleWidth] = useState(52)
    const [gotoCurrentButtonVisible, setGotoCurrentButtonVisible] = useState(false)
    const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false)
    const firstTimeDataLoaded = useRef(false)

    const measureScaleDims = useCallback(() => {
        const api = chart.current?.chartApi
        if (!api) return
        try {
            const width = api.priceScale('right').width()
            const height = api.timeScale().height()
            const measuredLeftScaleWidth = chart.current?.getLeftScaleWidth() ?? 0
            setScaleDims((prev) => {
                const w = width > 0 ? width : prev.width
                const h = height > 0 ? height : prev.height
                return w === prev.width && h === prev.height ? prev : { width: w, height: h }
            })
            setLeftScaleWidth((prev) => {
                const next = measuredLeftScaleWidth > 0 ? measuredLeftScaleWidth : prev
                return next === prev ? prev : next
            })
        } catch {
            // chart not ready yet
        }
    }, [])
    const prevViewportCandleInfoRef = useRef<ViewportCandleInfo | null>(null)
    const multiStrikeChartRef = useMemo(() => {
        if (optionsExpiries?.length && expiry && multiStrikeChartSecurities) {
            return Object.fromEntries(
                Object.entries(multiStrikeChartSecurities!!).map(([strike, callPutArray]) => {
                    const finalExpiry = optionsExpiries?.find((o) => o.expiry.name === expiry)
                    const isDecimal = finalExpiry?.strikes.some((s) => !Number.isInteger(s.strike / (security?.priceMultiplier ?? 100)))
                    const strikes = finalExpiry?.strikes.find((st) => (isDecimal ? (st.strike / 100).toFixed(2) : JSON.stringify(st.strike / 100)) === strike)
                    return [
                        strike,
                        {
                            callSecKey: strikes?.callSecurity ? getSecurityKey(strikes.callSecurity.exchange, strikes.callSecurity.code) : '',
                            putSecKey: strikes?.putSecurity ? getSecurityKey(strikes.putSecurity.exchange, strikes.putSecurity.code) : '',
                            callPut: callPutArray
                        }
                    ]
                })
            )
        }
        return
    }, [expiry, multiStrikeChartSecurities, optionsExpiries])

    const sendConfigToNative = useCallback(debounce(500, (config: string) => {
        callNative('onMultistrikeConfigChange', config)
    }), [])

    useEffect(() => {
        if (isEmbedded) {
            const config = {
                key: secKey,
                expiry: expiryObj,
                securities: multiStrikeChartSecurities,
                chartType,
                cumOi,
                nearAtm,
                atm,
                showChange,
                timeBucket
            }
            sendConfigToNative(JSON.stringify(config))
        }
    }, [sendConfigToNative, isEmbedded, secKey, expiryObj, multiStrikeChartSecurities, chartType, cumOi, nearAtm, atm, showChange, timeBucket])


    const timeRangeChangeListener = useCallback(
        (viewportCandleInfo: ViewportCandleInfo) => {
            setGotoCurrentButtonVisible(viewportCandleInfo.barsAfter > 0)
            if (!prevViewportCandleInfoRef.current || !areTwoRangesAlmostSame(prevViewportCandleInfoRef.current, viewportCandleInfo, chart.current?.chartApi.timeScale()!!)) {
                onTimeRangeChange && onTimeRangeChange(viewportCandleInfo, chart.current?.notifyGoToDateToChart ? chart.current?.gotoDate : undefined)
                prevViewportCandleInfoRef.current = viewportCandleInfo
            }
        },
        [onTimeRangeChange]
    )

    const onGotoCurrentButtonClick = useCallback(() => {
        chart.current?.gotoCurrentTime()
    }, [])

    const handleLoading = useCallback(
        (loadingValue: boolean) => {
            if (!loadingValue) {
                setHasCompletedInitialLoad(true)
            }
            if (isWidget) {
                dispatch(setChartLoading({ viewKey, loading: loadingValue }))
            } else {
                dispatch(setMultiStrikeChartLoading(loadingValue))
            }
        },
        [dispatch, isWidget, viewKey]
    )

    const showChartSkeleton = !hasCompletedInitialLoad

    useEffect(() => {
        if (chart.current) {
            chart.current.updateTimeRangeChangeListener(timeRangeChangeListener)
        }
    }, [timeRangeChangeListener])

    useEffect(() => {
        if (secKey !== multiStikeChartKey) {
            if (isWidget) {
                dispatch(
                    updateMultiStrikeChartConfig({
                        viewKey,
                        config: {
                            key: secKey,
                            securities: {}
                        }
                    })
                )
            } else {
                dispatch(setMultiStrikeSecurities({}))
                dispatch(setMultiStrikeChartKey(secKey))
            }
        }
    }, [dispatch, secKey, multiStikeChartKey])

    useEffect(() => {
        ;(async () => {
            if (chart.current && secKey) {
                chart.current.dataManager.removeAllSecondarySecurities(true)
                chart.current.primary = secKey
                await chart.current.fetchData(true, true)
                firstTimeDataLoaded.current = false
            }
        })()
    }, [secKey])

    useEffect(() => {
        if (chart.current) {
            chart.current.callPutSelected = callPut ?? ['CE', 'PE']
            chart.current.updateCumOiCallPutVisible()
        }
    }, [callPut])

    useEffect(() => {
        if (chart.current) {
            chart.current.showChange = showChange
            chart.current.dataManager.recalculateOiChangeSeriesForAllData()
            chart.current.onDataLoaded(DataUpdateRequestType.HISTORICAL, false)
        }
    }, [showChange])

    useEffect(() => {
        const secondarySecurities = Object.keys(multiStrikeChartRef ?? {})
            .map((strike) => {
                const sec = multiStrikeChartRef?.[strike]
                const keys = [sec?.callSecKey, sec?.putSecKey].filter((k): k is string => Boolean(k))
                return keys.map((key) => ({
                    exchange: exchangeFromJSON(key.split(':')[0]),
                    code: key.split(':')[1]
                }))
            })
            .flat()
        const list = [
            {
                exchange: exchangeFromJSON(secKey.split(':')[0]),
                code: secKey.split(':')[1]
            },
            ...secondarySecurities
        ]
        websocketActions.setWebsocketSubscribeList(dispatch, {
            candleSubscriptions: [
                {
                    tag: subscriptionTags.MULTI_STRIKES_CHART,
                    securities: list,
                    timeBucket: { timeSeries: 1, value: 1 }
                }
            ]
        })
    }, [multiStrikeChartRef, secKey, dispatch])

    useEffect(() => {
        if (!isWidget || !timeBucket || !timeBuckets?.length) return
        const resolved = resolveMultiStrikeTimeBucket(timeBucket, timeBuckets)
        if (resolved && resolved.code !== timeBucket.code) {
            dispatch(updateChartTimeBucket({ viewKey, timeBucket: resolved }))
        }
    }, [isWidget, timeBucket, timeBuckets, viewKey, dispatch])

    useEffect(() => {
        ;(async () => {
            if (chart.current && timeBucket) {
                chart.current.timeBucket = timeBucket
                await chart.current.fetchData(true, true)
            }
        })()
    }, [timeBucket])

    useEffect(() => {
        const currentExpiryIndex = optionsExpiries?.filter((o) => !o.derived).findIndex((o) => o.expiry.name === expiry)
        if (optionsExpiries && optionsExpiries?.length > 0 && (currentExpiryIndex ?? -1) < 0) {
            const tempExpiry = optionsExpiries?.filter((o) => !o.derived)[0].expiry.name
            if (tempExpiry) {
                if (isWidget) {
                    dispatch(updateMultiStrikeChartConfig({ viewKey, config: { expiry: tempExpiry } }))
                } else {
                    searchParams.set('expiry', tempExpiry)
                    setSearchParams(searchParams, { replace: true })
                }
            }
        }
    }, [secKey, optionsExpiries])

    const handleAtmStrikeClick = useCallback(
        async (i: number, isNearAtm: boolean, refresh = false) => {
            if (chart.current) {
                let optionExpiriesData = optionsExpiries
                let expiryIndex = refresh ? 0 : optionExpiriesData?.findIndex((o) => o.expiry.name === expiry)
                if (expiryIndex === -1 || !optionExpiriesData?.[expiryIndex as number]) {
                    expiryIndex = 0
                    const defaultExpiry = optionExpiriesData?.[expiryIndex]?.expiry?.name

                    if (defaultExpiry) {
                        dispatch(
                            updateMultiStrikeChartConfig({
                                viewKey,
                                config: {
                                    expiry: defaultExpiry,
                                    securities: {}
                                }
                            })
                        )
                    }
                }

                const expiryDataFinal = optionExpiriesData?.[expiryIndex as number]
                if (expiryDataFinal) {
                    const underlyingLtp = getSecuritySnapshot(underlyingToken)?.lastTradedPrice
                    const atmStrike = findNearestNumber(expiryDataFinal.strikesArray, underlyingLtp)
                    const atm = expiryDataFinal.strikes.find((s) => s.strike === atmStrike)
                    const isDecimal = expiryDataFinal.strikes.some((s) => !Number.isInteger(s.strike / (security?.priceMultiplier ?? 100)))
                    const atmIndex = expiryDataFinal.strikes.findIndex((s) => s.strike === atmStrike)

                    if (atm) {
                        const { strike, strikeData } = getStrikeAndStrikeData(atm, security?.priceMultiplier ?? 100, isDecimal)
                        let data = {
                            [strike]: strikeData
                        }
                        if (i > 0) {
                            const a = [3, 5, 7, 9]
                            a.forEach((x) => {
                                if (i >= x) {
                                    const index = x === 3 ? 1 : x === 5 ? 2 : x === 7 ? 3 : 4
                                    const atm1 = expiryDataFinal.strikes[atmIndex - index]
                                    const atm2 = expiryDataFinal.strikes[atmIndex + index]

                                    if (atm1 && atm2) {
                                        const { strike: strike1, strikeData: strikeData1 } = getStrikeAndStrikeData(atm1, security?.priceMultiplier ?? 100, isDecimal)
                                        const { strike: strike2, strikeData: strikeData2 } = getStrikeAndStrikeData(atm2, security?.priceMultiplier ?? 100, isDecimal)
                                        data = {
                                            [strike1]: strikeData1,
                                            ...data,
                                            [strike2]: strikeData2
                                        }
                                    }
                                }
                            })
                        } else {
                            data = {
                                ...multiStrikeChartRef
                            }
                        }
                        const formattedData: MultiStrikeSecuritiesType = Object.fromEntries(Object.entries(data).map(([key, value]) => [key, value.callPut]))
                        if (isWidget) {
                            dispatch(
                                updateMultiStrikeChartConfig({
                                    viewKey,
                                    config: {
                                        ...(isNearAtm ? { nearAtm: i } : { securities: formattedData }),
                                        securities: formattedData,
                                        expiry: expiryDataFinal.expiry.name
                                    }
                                })
                            )
                        } else {
                            dispatch(setMultiStrikeSecurities(formattedData))
                        }
                        chart.current.dataManager.addNewStrikes(data)
                        chart.current.onCrosshairMove({} as any)
                    }
                }
            }
        },
        [optionsExpiries, expiry, underlyingToken, security?.priceMultiplier, isWidget, dispatch, viewKey, multiStrikeChartRef]
    )

    useEffect(() => {
        if (!chart.current?.dataManager?.dataLoading && multiStrikeChartRef && Object.keys(multiStrikeChartRef).length === 0) {
            if (secKey === chart.current?.primary) {
                handleAtmStrikeClick(nearAtm ?? 3, nearAtm ? true : false)
            }
        }
    }, [expiry, optionsExpiries, chart?.current?.dataManager.dataLoading, handleAtmStrikeClick, nearAtm])

    const handleCrosshairMove = useCallback(
        (data?: CrossHairInfo) => {
            if (chart.current) {
                if (data?.point) {
                    setMultiStrikeChartCrosshairSnapshot(
                        viewKey,
                        buildCrosshairMultiStrikeStringSnapshot(chart.current)
                    )
                } else {
                    setMultiStrikeChartCrosshairSnapshot(viewKey, null)
                    setMultiStrikeChartLiveSnapshot(
                        viewKey,
                        buildLatestMultiStrikeStringSnapshot(chart.current)
                    )
                }
                bumpMultiStrikeCrosshairRevision(viewKey)
            }
            onCrosshairMove?.(data)
        },
        [onCrosshairMove, viewKey]
    )

    const lastExpiry = useRef<string | null>(expiry ?? null)
    useEffect(() => {
        if (chart.current && !chart.current?.dataManager?.dataLoading && expiry && (!firstTimeDataLoaded.current || lastExpiry.current !== expiry) && optionsExpiries && optionsExpiries.length > 0) {
            if (nearAtm) {
                handleAtmStrikeClick(nearAtm, true)
            } else if (Object.keys(multiStrikeChartSecurities!!).length > 0) {
                handleAtmStrikeClick(-1, false)
            } else {
                handleAtmStrikeClick(3, false)
            }
            firstTimeDataLoaded.current = true
            lastExpiry.current = expiry ?? null
            chart.current.resetTimeScale()
        }
    }, [expiry, chart.current?.dataManager.dataLoading, handleAtmStrikeClick, nearAtm, multiStrikeChartSecurities, optionsExpiries])

    useEffect(() => {
        ;(async () => {
            if (chart.current && !chart.current?.dataManager?.dataLoading) {
                chart.current.resetTimeScale()
            }
        })()
    }, [expiry])

    const theme = useTheme()
    const layoutOptions = useMemo(() => {
        const canvasLayout = buildCanvasLayoutPartial(canvasSettings, {
            textSecondary: theme.palette.text.secondary,
            divider: theme.palette.divider,
        }, {
            alignLabels: scalesAndLinesSettings.alignLabels ?? true,
            scaleMargins: CHART_PRICE_SCALE_MARGINS,
        })
        return {
            ...chartLayoutOptions,
            ...canvasLayout,
            layout: {
                background: {
                    type: ColorType.Solid,
                    color: theme.palette.background.paper
                },
                fontFamily: `'Work Sans', sans-serif`,
                ...canvasLayout.layout,
            },
            timeScale: {
                borderColor: theme.palette.divider,
                shiftVisibleRangeOnNewBar: true,
                allowShiftVisibleRangeOnWhitespaceReplacement: true,
                ...canvasLayout.timeScale,
            },
            rightPriceScale: {
                ...canvasLayout.rightPriceScale,
                visible: true,
                mode: PriceScaleMode.Normal,
                borderVisible: canvasLayout.rightPriceScale?.borderVisible ?? false,
            },
            leftPriceScale: {
                ...canvasLayout.leftPriceScale,
                visible: true,
                mode: PriceScaleMode.Normal,
                borderVisible: canvasLayout.leftPriceScale?.borderVisible ?? false,
            },
            crosshair: canvasLayout.crosshair,
        }
    }, [theme, scalesAndLinesSettings.alignLabels, canvasSettings])

    useEffect(() => {
        if (chart.current) {
            chart.current.scalesAndLinesSettings = scalesAndLinesSettings
            chart.current.applyScalesAndLinesSettings()
        }
    }, [scalesAndLinesSettings])

    useEffect(() => {
        const chart = appContext.getMultiStrikeChart(viewKey)

        if (chart) {
            chart.chartType = chartType
            chart.showChange = showChange
            chart.onDataLoaded(DataUpdateRequestType.LIVE, true, false, false)
            chart.dataManager.fetchData({})
            chart.onCrosshairMove({} as any)
        }
    }, [viewKey, chartType, showChange])

    useEffect(() => {
        if (chart.current) {
            chart.current.theme = theme.palette.mode
            chart.current.dataManager.seriesApis[chart.current.primary]?.updateOptions({
                color: theme.palette.mode === 'dark' ? primarySecColorDarkTheme : primarySecColorLightTheme
            })
            chart.current.applySeriesPaletteColors()
        }
    }, [theme, security])

    useEffect(() => {
        chart.current && chart.current.updateLayoutOptions(layoutOptions)
    }, [layoutOptions])

    useEffect(() => {
        if (chart.current) {
            chart.current.chartStyles = chartStyles
            chart.current.applyNewStyles()
        }
    }, [chartStyles])

    useEffect(() => {
        return () => {
            clearMultiStrikeChartSnapshot(viewKey)
            appContext.removeMultiStrikeChart(viewKey)
            clearMultiStrikeMetadataUpdaters(viewKey)
            const chartInstance = chart.current
            chart.current = undefined
            requestAnimationFrame(() => {
                chartInstance?.destroy()
            })
        }
    }, [appContext, viewKey])

    useEffect(() => {
        if (!container || chart.current) return

        chart.current = new Chart({
            id: 'chart-multi-strikes',
            viewKey,
            container,
            layoutOptions,
            primary: secKey,
            tradingCalendarData: calendarData,
            secondarySecurities: multiStrikeChartRef ?? {},
            callPutSelected: callPut ?? ['CE', 'PE'],
            showChange: showChange,
            timeBucket,
            timeBuckets,
            dateRange: undefined,
            persistViewport: persistViewport || false,
            theme: theme.palette.mode,
            chartType,
            ws: ws as WebSocketContextValue,
            subscriptionTag: subscriptionTags.MULTI_STRIKES_CHART,
            chartStyles,
            scalesAndLinesSettings,
            emptyTimeslotsSize: 498,
            isEmbedded: isEmbedded,
            onDataUpdate() {
                if (!chart.current) return
                measureScaleDims()
                const latestSnapshot = buildLatestMultiStrikeStringSnapshot(chart.current)
                setMultiStrikeChartLiveSnapshot(viewKey, latestSnapshot)
                if (chart.current.crossHairInfo?.point) {
                    setMultiStrikeChartCrosshairSnapshot(
                        viewKey,
                        buildCrosshairMultiStrikeStringSnapshot(chart.current)
                    )
                } else {
                    setMultiStrikeChartCrosshairSnapshot(viewKey, null)
                }
                bumpMultiStrikeCrosshairRevision(viewKey)
            },
            onCrosshairMove: handleCrosshairMove,
            onTimeRangeChange: timeRangeChangeListener,
            onLoading: handleLoading,
            timezone
        })

        chart.current.chartApi.timeScale().applyOptions({
            timeVisible: true,
            secondsVisible: true,
            shiftVisibleRangeOnNewBar: true,
            allowShiftVisibleRangeOnWhitespaceReplacement: true,
        })

        appContext.setMultiStrikeChart(viewKey, chart.current)
        requestAnimationFrame(measureScaleDims)
    }, [container, security])

    useEffect(() => {
        if (!container) return
        let raf = 0
        const scheduleMeasure = () => {
            cancelAnimationFrame(raf)
            raf = requestAnimationFrame(measureScaleDims)
        }
        scheduleMeasure()
        const timeoutId = setTimeout(scheduleMeasure, 300)
        const observer = new ResizeObserver(scheduleMeasure)
        if (chartRef.current) observer.observe(chartRef.current)
        return () => {
            cancelAnimationFrame(raf)
            clearTimeout(timeoutId)
            observer.disconnect()
        }
    }, [container, measureScaleDims, loading, chartType, theme.palette.mode])

    useEffect(() => {
        if (chart.current) {
            chart.current.updateCrossHairMoveCallback(handleCrosshairMove)
        }
    }, [handleCrosshairMove])

    useEffect(() => {
        if (chart.current) {
            chart.current.updateTradingCalendarData(calendarData)
        }
    }, [calendarData])

    useEffect(() => {
        if (!container) return
        const handleFullscreenChange = () => {
            if (document.fullscreenElement) return
            requestAnimationFrame(() => {
                if (!chart.current?.chartApi || !container) return
                chart.current.chartApi.resize(container.clientWidth, container.clientHeight, true)
            })
        }
        document.addEventListener('fullscreenchange', handleFullscreenChange)
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }, [container])

    const chartContainerStyle: React.CSSProperties = useMemo(
        () => ({
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        }),
        []
    )

    const gotoCurrentDateTooltipContent = useMemo(
        () => (
            <ScrollToRecentBarButton
                visible={gotoCurrentButtonVisible}
                onClick={onGotoCurrentButtonClick}
                priceScaleWidth={scaleDims.width}
                timeScaleHeight={scaleDims.height}
            />
        ),
        [gotoCurrentButtonVisible, onGotoCurrentButtonClick, scaleDims.width, scaleDims.height]
    )

    return (
        <div
            className={`chart-container font-feature-num rounded-none border-none`}
            style={isWidget ? undefined : { flex: 1, minHeight: 0 }}
            key={'chart-container'}
        >
            <div style={chartContainerStyle}>
                {isWidget && viewKey ? <ChartSettingsButtons viewKey={memoizedViewKey} allowSplit={allowSplit} security={security} /> : null}
                <div className="chart" ref={chartRef} style={{ position: 'relative' }}>
                    {showChartSkeleton ? <MultiStrikeChartSkeletonOverlay /> : null}
                    <div ref={handleRef} id={`chart-${viewKey}`} style={{ cursor: 'crosshair' }} className="m-0 h-full w-full p-0" />
                    {gotoCurrentDateTooltipContent}
                    <NonInteractiveTooltip title="Settings" placement="left-start">
                        <Button
                            variant="text"
                            color="inherit"
                            disableRipple
                            onClick={() => setOpenSettings(true)}
                            sx={{
                                position: 'absolute',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bottom: 0,
                                right: 0,
                                left: 'auto',
                                width: scaleDims.width,
                                minWidth: 52,
                                height: scaleDims.height,
                                minHeight: scaleDims.height,
                                maxHeight: scaleDims.height,
                                p: 0,
                                borderRadius: 0,
                                zIndex: 1
                            }}
                        >
                            <SettingsOutlinedIcon sx={{ width: 20, height: 20 }} />
                        </Button>
                    </NonInteractiveTooltip>
                </div>
                {container && multiStrikeChartRef && hasCompletedInitialLoad ? (
                    <ChartMetadata
                        viewKey={viewKey}
                        container={container}
                        multiStrikeClassType={multiStrikeChartRef}
                        leftScaleWidth={leftScaleWidth}
                        displayCumulativeToggle={displayCumulativeToggle}
                        onSecuritySelected={onSecuritySelected}
                    />
                ) : null}
                {openSettings ? (
                    <MultiStrikeSettingsDialog open={openSettings} onClose={() => setOpenSettings(false)} viewKey={viewKey} />
                ) : null}
            </div>
        </div>
    )
})

ChartWrapper.displayName = 'ChartWrapper'

export default memo(ChartWrapper)
